import { useState } from "react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";

export default function Eligibility() {
  const [form, setForm] = useState({ age: "", income: "", employment: "salaried", city: "" });
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const check = () => {
    const age = parseInt(form.age || "0", 10);
    const income = parseFloat(form.income || "0");
    const rules = [
      { pass: age >= 21 && age <= 60, msg: "Age between 21 and 60" },
      { pass: income >= 20000, msg: "Monthly income ≥ ₹20,000" },
      { pass: form.employment === "salaried", msg: "Salaried employment" },
      { pass: !!form.city, msg: "Serviceable city (illustrative)" },
    ];
    const eligible = rules.every((r) => r.pass);
    const suggested = eligible ? Math.min(500000, income * 10) : 0;
    setResult({ rules, eligible, suggested });
  };

  return (
    <MarketingLayout>
      <section className="page-shell px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Eligibility Checker</div>
          <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-[1.05]">Find out in 30 seconds.</h1>
          <p className="text-lg text-muted-foreground mt-6 max-w-md">This calculator gives an indicative eligibility. Final decisions are subject to internal underwriting.</p>
          <ul className="mt-6 space-y-2 text-sm">
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> No CIBIL impact</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Purely indicative</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Data stays on your device</li>
          </ul>
        </div>
        <Card className="p-8 rounded-3xl">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="age">Age</Label>
              <Input id="age" data-testid="elig-age" type="number" value={form.age} onChange={set("age")} placeholder="e.g. 29" />
            </div>
            <div>
              <Label htmlFor="income">Monthly income</Label>
              <Input id="income" data-testid="elig-income" type="number" value={form.income} onChange={set("income")} placeholder="e.g. 60000" />
            </div>
            <div>
              <Label>Employment</Label>
              <select className="w-full h-10 px-3 rounded-md border border-input bg-background" data-testid="elig-employment" value={form.employment} onChange={set("employment")}>
                <option value="salaried">Salaried</option>
                <option value="self">Self-employed</option>
              </select>
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" data-testid="elig-city" value={form.city} onChange={set("city")} placeholder="e.g. Bengaluru" />
            </div>
          </div>
          <Button onClick={check} className="w-full mt-6 rounded-full brand-gradient text-white" data-testid="elig-check">Check my eligibility</Button>

          {result && (
            <div className="mt-6 space-y-4">
              <div className="space-y-2 text-sm">
                {result.rules.map((r) => (
                  <div key={r.msg} className="flex items-center gap-2">
                    {r.pass ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                    <span className={r.pass ? "" : "text-muted-foreground"}>{r.msg}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl bg-muted p-5 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Indicative amount</div>
                  <div className="font-display text-3xl">{result.eligible ? `₹${result.suggested.toLocaleString("en-IN")}` : "—"}</div>
                </div>
                <Link to="/register"><Button className="rounded-full brand-gradient text-white">Apply Now</Button></Link>
              </div>
            </div>
          )}
        </Card>
      </section>
    </MarketingLayout>
  );
}
